import React, { createContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { enhancedTokenManager } from '../services/EnhancedTokenManager';
import { config } from '../config/environment';
import { authStateManager } from '../utils/authStateManager';

// User interface (extended for multi-role support)
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;  // Deprecated - kept for backward compatibility
  roles?: Array<{  // NEW: Multiple roles
    id: string;
    role_type: 'SUPERADMIN' | 'ADMIN' | 'PASTOR' | 'MINISTER' | 'LEADER' | 'BISHOP' | 'SUPERINTENDENT';
    church?: {
      id: string;
      name: string;
    };
    ministries?: Array<{ id: string; name: string; }>;
    groups?: Array<{ id: string; name: string; }>;
    services?: Array<{ id: string; name: string; }>;
    is_primary: boolean;
    is_active: boolean;
    expires_date?: string;
  }>;
  primaryRole?: {  // NEW: Primary role for display
    role_type: string;
    church?: {
      id: string;
      name: string;
    };
  };
  churchId?: string;
  is_superuser?: boolean;  // Superuser flag for admin privileges
  preferences?: UserPreferences;
  test_account?: {  // Test account info when active (superusers only)
    id: string;
    name: string;
    role_type: string;
    church?: {
      id: string;
      name: string;
    };
  };
}

// User preferences interface
export interface UserPreferences {
  theme: {
    mode: 'light' | 'dark' | 'system';
    primaryColor?: string;
    fontSize?: string;
    activePaletteId?: string;
    customPalette?: any; // Will use CustomPalette type from user.ts
    savedPalettes?: any[]; // Will use SavedPalette[] type from user.ts
  };
  notifications: boolean;
  emailFrequency: 'daily' | 'weekly' | 'monthly' | 'never';
  language: string;
  accessibility?: {
    highContrast: boolean;
    reducedMotion: boolean;
    largeText: boolean;
  };
  notificationChannels?: {
    email: boolean;
    push: boolean;
    inApp: boolean;
  };
  selectedChurchId?: string; // PRODUCTION: Selected church stored in backend
}

// Register data interface
export interface RegisterData {
  email: string;
  password: string;
  password_confirm: string;
  first_name: string;
  last_name: string;
  church?: string;
  role?: string;
}

// Auth context interface - exported for useAuth hook
export interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenExpiresIn: number;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
  // New role-related helpers
  hasRole: (roleType: string) => boolean;
  hasAnyRole: (roleTypes: string[]) => boolean;
  canAccess: (resource: string, action?: string) => boolean;
  getPrimaryRole: () => string | null;
  getChurchId: () => string | null;
  setSelectedChurchId: (churchId: string) => Promise<void>;
}

// Create the auth context - exported for useAuth hook
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: React.ReactNode;
}

// Mock user data
const mockUser: User = {
  id: 'user1',
  email: 'user@example.com',
  name: 'John Doe',
  role: 'admin',
  // churchId should be populated from the database when user logs in
  churchId: undefined,
  preferences: {
    theme: 'light',
    notifications: true,
    emailFrequency: 'weekly',
    language: 'en',
    accessibility: {
      highContrast: false,
      reducedMotion: false,
      largeText: false
    },
    notificationChannels: {
      email: true,
      push: true,
      inApp: true
    }
  }
};

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize user from EnhancedTokenManager if available and session is active
  const initializeUser = (): User | null => {
    // Use authStateManager which internally uses EnhancedTokenManager
    return authStateManager.getUser();
  };
  
  // Always initialize from authStateManager first
  const initialUser = authStateManager.getUser() || initializeUser();
  const [user, setUser] = useState<User | null>(initialUser);
  const [loading, setLoading] = useState<boolean>(false); // Start with not loading since we check synchronously
  const [error, setError] = useState<string | null>(null);
  const [tokenExpiresIn, setTokenExpiresIn] = useState(0);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  
  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = authStateManager.subscribe((newUser) => {
      setUser(newUser);
      if (!newUser) {
        setLoading(false);
      }
    });
    
    // Check auth on mount only if needed
    if (!authStateManager.hasActiveSession() && !hasCheckedAuth) {
      checkAuth();
      setHasCheckedAuth(true);
    } else if (authStateManager.hasActiveSession()) {
      // We have an active session, ensure state is synced
      setUser(authStateManager.getUser());
      setLoading(false);
      updateTokenExpiration();
    }
    
    // Listen for token refresh events
    const handleTokenRefresh = () => {
      updateTokenExpiration();
    };
    
    // Listen for token expiration
    const handleTokenExpired = () => {
      console.log('[AuthContext] Token expired, logging out');
      sessionStorage.removeItem('connectpath_active_session');
      setUser(null);
    };
    
    window.addEventListener('tokenRefreshed', handleTokenRefresh);
    window.addEventListener('tokenExpired', handleTokenExpired);
    
    // Smart token expiration updates - more frequent when near expiration
    let intervalId: NodeJS.Timeout;

    const smartUpdateInterval = () => {
      const timeLeft = enhancedTokenManager.getTimeUntilExpiration();
      const timeLeftSeconds = Math.floor(timeLeft / 1000);

      // If less than 5 minutes remain, update every second for countdown
      // Otherwise update every 60 seconds (increased from 30 to reduce re-renders)
      const updateInterval = timeLeftSeconds <= 300 ? 1000 : 60000;

      clearInterval(intervalId);
      intervalId = setInterval(() => {
        updateTokenExpiration();
        smartUpdateInterval(); // Re-evaluate interval
      }, updateInterval);
    };

    // Initial update on mount
    updateTokenExpiration();
    smartUpdateInterval();

    return () => {
      unsubscribe();
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
      window.removeEventListener('tokenExpired', handleTokenExpired);
      clearInterval(intervalId);
    };
  }, []); // Empty dependency array - only run once on mount

  const updateTokenExpiration = () => {
    const timeLeft = enhancedTokenManager.getTimeUntilExpiration();
    setTokenExpiresIn(Math.floor(timeLeft / 1000)); // Convert to seconds
  };

  const checkAuth = async () => {
    try {
      // Don't set loading if we already have a user (prevents flicker during navigation)
      if (!user) {
        setLoading(true);
      }
      
      // Check for active session flag (survives navigation but not browser close)
      const hasActiveSession = sessionStorage.getItem('connectpath_active_session') === 'true';
      const storedUser = authStateManager.getUser();
      
      // If no active session, check token validity
      const tokenValid = enhancedTokenManager.isTokenValid();
      
      if (config.enableDebugLogging) {
        console.log('[AuthContext] checkAuth:', {
          tokenValid,
          hasActiveSession,
          hasUser: !!storedUser
        });
      }
      
      if (tokenValid && storedUser) {
        // Fetch fresh preferences on page load
        try {
          const preferences = await authService.getPreferences();
          storedUser.preferences = preferences;
          if (config.enableDebugLogging) {
            console.log('[AuthContext] Fetched preferences on refresh:', preferences);
          }
        } catch (prefError: any) {
          console.error('[AuthContext] Failed to fetch preferences on refresh:', prefError.message || prefError);
        }

        // Update both state and storage with fetched preferences
        authStateManager.setUser(storedUser);
        setUser(storedUser);
        updateTokenExpiration();
        // Mark session as active
        sessionStorage.setItem('connectpath_active_session', 'true');
      } else {
        // No valid token and no active session
        // Only clear user if we don't have an active session
        if (!hasActiveSession) {
          setUser(null);
          sessionStorage.removeItem('connectpath_active_session');
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Login function
  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authService.login(email, password);
      
      // Initialize enhanced token manager with the new token
      enhancedTokenManager.setToken(response.access_token, response.expires_at);
      
      // Transform the API response to match AuthContext User interface
      const transformedUser: User = {
        id: response.user.id,
        email: response.user.email,
        name: response.user.name || '', // API already provides combined name
        role: response.user.role,
        roles: response.user.roles,  // Include roles array
        primaryRole: response.user.primary_role,  // Include primary role
        // Transform snake_case church_id to camelCase churchId
        // Handle church as either string (church_id) or object ({id, name})
        churchId: response.user.church_id ||
                  (typeof response.user.church === 'object' ? response.user.church?.id : response.user.church) ||
                  undefined,
        is_superuser: response.user.is_superuser,  // Include superuser flag
        preferences: {} // Will fetch preferences separately
      };

      // Fetch user preferences separately (not included in login response)
      try {
        const preferences = await authService.getPreferences();
        transformedUser.preferences = preferences;
        if (config.enableDebugLogging) {
          console.log('[AuthContext] Fetched preferences:', preferences);
        }
      } catch (prefError: any) {
        console.error('[AuthContext] Failed to fetch preferences:', prefError.message || prefError);
        // Continue with empty preferences - non-critical
      }

      // Set user data through manager (handles storage)
      authStateManager.setUser(transformedUser);
      setUser(transformedUser);
      updateTokenExpiration();

      // NOTE: ChurchSwitcher component will handle fetching accessible churches
      // and setting selectedChurchId. We don't set it here to avoid race conditions.
      // The getChurchId() function will read from localStorage which ChurchSwitcher populates.
      console.log('[AuthContext] Login successful - ChurchSwitcher will handle church selection');
      
    } catch (err: any) {
      console.error('Login error:', err);
      const message = err.response?.data?.error?.message || 'Invalid email or password';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Register function
  const register = async (data: RegisterData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authService.register(data);
      
      // Initialize enhanced token manager with the new token
      enhancedTokenManager.setToken(response.access_token, response.expires_at);
      
      // Transform the API response to match AuthContext User interface
      const transformedUser: User = {
        id: response.user.id,
        email: response.user.email,
        // Handle both formats: either 'name' field or separate first/last names
        name: response.user.name || `${response.user.first_name || ''} ${response.user.last_name || ''}`.trim() || '',
        role: response.user.role,
        roles: response.user.roles,  // Include roles array
        primaryRole: response.user.primary_role,  // Include primary role
        // Transform snake_case church_id to camelCase churchId
        // Handle church as either string (church_id) or object ({id, name})
        churchId: response.user.church_id ||
                  (typeof response.user.church === 'object' ? response.user.church?.id : response.user.church) ||
                  undefined,
        preferences: response.user.preferences
      };
      
      // Store user data in localStorage
      try {
        localStorage.setItem('connectpath_user', JSON.stringify(transformedUser));
      } catch (e) {
        console.error('Failed to store user data:', e);
      }
      
      // Set user data
      setUser(transformedUser);
      updateTokenExpiration();

      // NOTE: ChurchSwitcher component will handle fetching accessible churches
      // and setting selectedChurchId for new users.
      console.log('[AuthContext] Registration successful - ChurchSwitcher will handle church selection');
      
    } catch (err: any) {
      console.error('Registration error:', err);
      const message = err.response?.data?.error?.message || 'Registration failed. Please try again.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Logout function
  const logout = async () => {
    try {
      // Step 1a: Call Cornerstone platform logout (browser-side with cookies)
      // This clears .ministry.builders shared cookies per platform spec
      try {
        await fetch('https://cornerstone.ministry.builders/api/v1/auth/logout', {
          method: 'POST',
          credentials: 'include',  // Required to send/clear .ministry.builders cookies
        });
      } catch {
        // Best-effort — always proceed with local cleanup
      }

      // Step 1b: Call ConnectPath backend logout to blacklist local JWT
      const refreshToken = localStorage.getItem('refresh_token');
      try {
        const token = await enhancedTokenManager.getToken();
        if (token) {
          await fetch('/api/v1/cornerstone/oauth/logout/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
        }
      } catch {
        // Best-effort
      }

      // Step 2: Call regular logout
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      enhancedTokenManager.clearToken();
      authStateManager.setUser(null);
      setUser(null);
      setTokenExpiresIn(0);

      // CRITICAL SECURITY: Clear all localStorage items on logout
      localStorage.removeItem('selectedContext');
      localStorage.removeItem('connectpath_user');
      localStorage.removeItem('assessmentDraft');
      localStorage.removeItem('knowledgeRecentSearches');
      localStorage.removeItem('impactDashboardCardOrder');
      localStorage.removeItem('isKioskMode');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('connectpath_use_mock_data');

      // Clear any cached church data from sessionStorage
      sessionStorage.removeItem('lastLoadedChurchId');
      sessionStorage.removeItem('connectpath_active_session');

      // Step 3: MUST use window.location.href (not React Router navigate)
      // This ensures full page reload, destroying all in-memory state
      window.location.href = '/login';
    }
  };
  
  // Forgot password function
  const forgotPassword = async (email: string) => {
    setLoading(true);
    setError(null);
    
    try {
      await authService.forgotPassword(email);
    } catch (err: any) {
      console.error('Error requesting password reset:', err);
      const message = err.response?.data?.error?.message || 'Failed to request password reset. Please try again later.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Reset password function
  const resetPassword = async (token: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Extract uid from the token or pass it separately
      // This needs to match the backend implementation
      const [uid, actualToken] = token.split(':');
      await authService.resetPassword(actualToken, uid, password, password);
    } catch (err: any) {
      console.error('Error resetting password:', err);
      const message = err.response?.data?.error?.message || 'Failed to reset password. Please try again later.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Update profile function
  const updateProfile = async (userData: Partial<User>) => {
    setLoading(true);
    setError(null);

    try {
      // Transform AuthContext User format to API format
      const apiUserData: any = {};

      // Handle name -> first_name/last_name transformation
      if (userData.name !== undefined) {
        const nameParts = userData.name.trim().split(' ');
        apiUserData.first_name = nameParts[0] || '';
        apiUserData.last_name = nameParts.slice(1).join(' ') || '';
      }

      // Handle direct first_name/last_name if provided (from UserProfileForm)
      if ((userData as any).first_name !== undefined) {
        apiUserData.first_name = (userData as any).first_name;
      }
      if ((userData as any).last_name !== undefined) {
        apiUserData.last_name = (userData as any).last_name;
      }

      // Copy other fields that match API format
      if (userData.email !== undefined) apiUserData.email = userData.email;
      if (userData.role !== undefined) apiUserData.role = userData.role;

      // Handle preferences separately using the dedicated endpoint
      if (userData.preferences !== undefined) {
        console.log('[AuthContext] Updating preferences:', userData.preferences);
        const apiAuthService = authService as any;
        if (apiAuthService.updatePreferences) {
          console.log('[AuthContext] Using updatePreferences method');
          await apiAuthService.updatePreferences(userData.preferences);
          // After updating preferences, fetch the updated ones from the backend
          // to ensure they are properly applied
          const updatedPreferences = await apiAuthService.getPreferences();
          console.log('[AuthContext] Fetched updated preferences:', updatedPreferences);
          userData.preferences = updatedPreferences;
        } else {
          console.log('[AuthContext] Fallback - including in profile update');
          // Fallback to including in profile update if the service doesn't have updatePreferences
          apiUserData.preferences = userData.preferences;
        }
      }

      // Only call updateProfile if we have non-preference data to update
      let response;
      console.log('[AuthContext] updateProfile - apiUserData:', apiUserData);
      if (Object.keys(apiUserData).length > 0) {
        response = await authService.updateProfile(apiUserData);
        console.log('[AuthContext] updateProfile - response:', response);
      } else if (userData.preferences !== undefined && user) {
        // If only preferences were updated, return current user with updated preferences
        response = { ...user, preferences: userData.preferences };
      } else {
        response = user || {} as User;
      }
      
      // Transform the API response to match AuthContext User interface
      const constructedName = response.name || `${response.first_name || ''} ${response.last_name || ''}`.trim() || 'Unnamed User';
      console.log('[AuthContext] updateProfile - constructing name:', { responseName: response.name, firstName: response.first_name, lastName: response.last_name, constructedName });
      const transformedUser: User = {
        id: response.id,
        email: response.email,
        name: constructedName,
        role: response.role,
        roles: response.roles,  // Include roles array
        primaryRole: response.primary_role,  // Include primary role
        // Transform snake_case church_id to camelCase churchId
        // Handle church as either string (church_id) or object ({id, name})
        churchId: response.church_id ||
                  (typeof response.church === 'object' ? response.church?.id : response.church) ||
                  undefined,
        // Preserve is_superuser flag from response or current user
        is_superuser: response.is_superuser !== undefined ? response.is_superuser : user?.is_superuser,
        // Preserve test_account from response or current user
        test_account: response.test_account || user?.test_account,
        // Use the updated preferences from userData if they were updated, otherwise use response
        preferences: userData.preferences !== undefined ? userData.preferences : response.preferences
      };
      
      // Update stored user data
      try {
        console.log('[AuthContext] Storing updated user with preferences:', transformedUser.preferences);
        localStorage.setItem('connectpath_user', JSON.stringify(transformedUser));
        // CRITICAL: Also update authStateManager to persist to sessionStorage
        authStateManager.setUser(transformedUser);
        console.log('[AuthContext] User data stored to localStorage and sessionStorage');
      } catch (e) {
        console.error('Failed to store user data:', e);
      }

      setUser(transformedUser);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      const message = err.response?.data?.error?.message || 'Failed to update profile. Please try again later.';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Role helper methods
  const hasRole = (roleType: string): boolean => {
    if (!user) return false;
    
    // Check new roles array first
    if (user.roles && user.roles.length > 0) {
      return user.roles.some(role => 
        role.role_type === roleType && role.is_active
      );
    }
    
    // Fallback to old role field for backward compatibility
    return user.role === roleType.toLowerCase();
  };

  const hasAnyRole = (roleTypes: string[]): boolean => {
    return roleTypes.some(roleType => hasRole(roleType));
  };

  const canAccess = (resource: string, action: string = 'view'): boolean => {
    if (!user) return false;
    
    // Simple permission check - can be expanded
    const adminRoles = ['SUPERADMIN', 'ADMIN', 'PASTOR'];
    
    if (hasAnyRole(adminRoles)) return true;
    
    // Add more granular permission checks here based on resource and action
    return false;
  };

  const getPrimaryRole = (): string | null => {
    if (!user) return null;
    
    // Check new roles array
    if (user.roles && user.roles.length > 0) {
      const primaryRole = user.roles.find(r => r.is_primary) || user.roles[0];
      return primaryRole.role_type;
    }
    
    // Fallback to old role field
    return user.role || null;
  };

  const getChurchId = (): string | null => {
    if (!user) {
      console.warn('[AuthContext] getChurchId called with no user - returning null');
      return null;
    }

    // PRIORITY 1: Use selected church from localStorage (for backward compatibility)
    // This allows users to switch between multiple churches they have access to
    const selectedChurchId = localStorage.getItem('selectedChurchId');
    if (selectedChurchId) {
      return selectedChurchId;
    }

    // PRIORITY 1.5: Check backend preferences (PRODUCTION upgrade path)
    if (user.preferences?.selectedChurchId) {
      return user.preferences.selectedChurchId;
    }

    // PRIORITY 2: Check primary role for church
    if (user.primaryRole?.church?.id) {
      console.warn('[AuthContext] No selectedChurchId in preferences, using primary role church');
      return user.primaryRole.church.id;
    }

    // PRIORITY 3: Check any role with church
    if (user.roles && user.roles.length > 0) {
      const roleWithChurch = user.roles.find(r => r.church?.id);
      if (roleWithChurch?.church?.id) {
        console.warn('[AuthContext] No selectedChurchId in preferences, using first role with church');
        return roleWithChurch.church.id;
      }
    }

    // FAIL: No church found anywhere
    console.error('[AuthContext] CHURCH_ID_ERROR: No church found in preferences, roles, or JWT token');
    console.error('[AuthContext] User data:', {
      email: user.email,
      hasRoles: !!user.roles,
      roleCount: user.roles?.length,
      hasPrimaryRole: !!user.primaryRole,
      jwtChurchId: user.churchId
    });
    throw new Error('CHURCH_ID_ERROR: User has no accessible church. Check ChurchStaff/ChurchAccess records and ensure login properly sets selectedChurchId.');
  };

  // Set selected church ID - saves to backend preferences (PRODUCTION-READY)
  const setSelectedChurchId = async (churchId: string): Promise<void> => {
    try {
      if (!user) {
        throw new Error('Cannot set selected church: No user logged in');
      }

      console.log('[AuthContext] Setting selected church to:', churchId);

      // Also save to localStorage for components that read from there (e.g., ImpactDashboardContext)
      localStorage.setItem('selectedChurchId', churchId);

      // Save to backend preferences
      const updatedPreferences = {
        ...user.preferences,
        selectedChurchId: churchId
      };

      await authService.updatePreferences(updatedPreferences);

      // Update local user state
      const updatedUser = {
        ...user,
        preferences: updatedPreferences
      };

      authStateManager.setUser(updatedUser);
      setUser(updatedUser);

      console.log('[AuthContext] Selected church saved to backend preferences');
    } catch (error) {
      console.error('[AuthContext] Failed to set selected church:', error);
      throw error;
    }
  };

  // Refresh user function - re-fetch user data from backend
  const refreshUser = async () => {
    try {
      // Check if we have a valid token
      const tokenValid = enhancedTokenManager.isTokenValid();
      if (!tokenValid) {
        console.error('No valid token available for refreshing user');
        return;
      }

      // Force fresh user data fetch from the backend by calling checkAuth
      await checkAuth();
      
      if (config.enableDebugLogging) {
        console.log('[AuthContext] User refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // Don't throw error - just log it, as this is typically called in background
    }
  };
  
  // Auth context value - memoized to prevent unnecessary re-renders
  // Note: tokenExpiresIn is intentionally excluded from dependencies to prevent
  // re-renders every time it updates (every 60 seconds)
  const value = React.useMemo(() => ({
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isLoading: loading,
    tokenExpiresIn,  // This updates frequently but most components don't need it
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    refreshUser,
    // New role helpers
    hasRole,
    hasAnyRole,
    canAccess,
    getPrimaryRole,
    getChurchId,
    setSelectedChurchId
  }), [
    user,
    loading,
    error,
    // Intentionally omitting tokenExpiresIn from dependencies
    // to prevent re-renders every 60 seconds
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    refreshUser,
    hasRole,
    hasAnyRole,
    canAccess,
    getPrimaryRole,
    getChurchId,
    setSelectedChurchId
  ]);
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Re-export useAuth from separate file for Fast Refresh compatibility
// This maintains backward compatibility for existing imports
export { useAuth } from './useAuth';
